import {
  PricingBundle,
  PricingBundleDoc,
  PricingBundleScope,
} from '../../interfaces/pricing/pricing-bundle.interface';
import { BundleSyncService } from '../app-ui/bundle-sync.service';
import { DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { EntityNames } from '../delta/delta.service';
import { Injectable } from '@angular/core';
import { PRICING_BUNDLE_SCHEMA } from './pricing-bundle.schema';

const PRICING_BUNDLE_ACTION = 'indskr_GetPricingBundle';
/** Ids per lookup, so the filter stays well inside the URL length limit. */
const CATEGORY_LOOKUP_CHUNK = 50;

/**
 * Pricing bundles from `indskr_GetPricingBundle`. Price lists are caller-filtered — the same list
 * at the same `version` carries different `items` for different subjects — so a bundle is cached
 * whole, one document per request projection, never deduped by price-list id.
 * See docs/features/order-capture.md.
 */
@Injectable({ providedIn: 'root' })
export class PricingDataService {
  /** What the last sync wrote, until taken: the caller that synced need not read and decrypt it back. */
  private written: { key: string; bundle: PricingBundle } | null = null;

  constructor(
    private readonly bundles: BundleSyncService,
    private readonly dynamics: DynamicsClientService,
  ) {}

  /**
   * Key for one request projection: every input that changes the payload is part of it. `AsOf`
   * is not — omitted, the server prices for today, and pinning a date would hide price changes.
   */
  public getProjectionKey(
    positionIds: string[],
    accountId: string | null,
    scope: PricingBundleScope,
  ): string {
    const positions = [...positionIds].sort().join(',');
    return `${DB_KEY_PREFIXES.PRICING_BUNDLE}_${scope}_${positions}_${accountId ?? 'none'}`;
  }

  /** Cached bundle for a projection, or null when it was never synced. */
  public async getCachedBundle(
    accountId: string | null,
    scope: PricingBundleScope = 'single',
    positionIds: string[] = this.bundles.positionIds(),
  ): Promise<PricingBundle | null> {
    return (
      (
        await this.bundles.cached<PricingBundleDoc>(
          this.getProjectionKey(positionIds, accountId, scope),
        )
      )?.bundle ?? null
    );
  }

  /**
   * The projection as the last sync of it wrote it, which is what the device holds (only a sync
   * writes it); null when taken already. Taken once, so the large bundle is not held after use.
   */
  public takeSyncedBundle(
    accountId: string | null,
    scope: PricingBundleScope = 'single',
    positionIds: string[] = this.bundles.positionIds(),
  ): PricingBundle | null {
    const written = this.written;
    if (written?.key !== this.getProjectionKey(positionIds, accountId, scope))
      return null;
    this.written = null;
    return written.bundle;
  }

  /** Syncs one projection; true when the device copy was replaced. */
  public async syncPricingBundle(
    accountId: string | null,
    scope: PricingBundleScope = 'single',
    positionIds: string[] = this.bundles.positionIds(),
    loadFromDbOnly = false,
  ): Promise<boolean> {
    if (loadFromDbOnly || this.bundles.isOffline) return false;
    const key = this.getProjectionKey(positionIds, accountId, scope);
    return this.bundles.sync<PricingBundle, PricingBundleDoc>({
      action: PRICING_BUNDLE_ACTION,
      entityName: EntityNames.pricingBundle,
      key,
      rawData: (cached) => ({
        subjects: this.bundles.subjects(positionIds, {
          account: accountId ? [accountId] : [],
        }),
        // Top-level, not a subject. Naming what the device holds is the only way to be told what
        // it lost: without it the server answers in "new" mode and a lost list just stops appearing.
        knownPriceLists: (cached?.bundle?.changed ?? []).map(
          (priceList) => priceList.id,
        ),
        scope,
        // Off by default server-side; cross-product offers are wanted here.
        includeUnmappedCrossProduct: true,
      }),
      stored: { scope, positionIds, accountId },
      schema: PRICING_BUNDLE_SCHEMA,
      enrich: async (bundle, cached) => this.withCategoryNames(bundle, cached),
      count: (bundle) => bundle.changed?.length ?? 0,
      afterWrite: (bundle) => {
        this.written = { key, bundle };
        // The swap already applied removals; a tombstone means an entitlement changed, worth a log line.
        if (!bundle.removed?.length) return;
        const removed = bundle.removed
          .map((r) => `${r.id} (${r.reason})`)
          .join(', ');
        console.info(
          `PricingDataService: ${bundle.removed.length} price list(s) no longer eligible for ${key}: ${removed}`,
        );
      },
    });
  }

  /**
   * Drops every cached projection but the current catalogue-wide one: the per-account documents
   * of the old per-account sync, and stale position sets after a realignment.
   */
  public async purgeStaleProjections(
    positionIds = this.bundles.positionIds(),
  ): Promise<void> {
    // With no positions loaded the "current" key is not the rep's; keeping only it would delete theirs.
    if (positionIds.length === 0) return;
    await this.bundles.purge(
      DB_KEY_PREFIXES.PRICING_BUNDLE,
      new Set([this.getProjectionKey(positionIds, null, 'all')]),
    );
  }

  /**
   * Categories are product families, which the rep's product sync never brings down, so the matrix
   * could not name its groups offline. Their names are looked up here and stored with the items.
   * A failed lookup keeps the names the device already had; it never fails the pricing sync.
   */
  private async withCategoryNames(
    bundle: PricingBundle,
    cached: PricingBundleDoc | null,
  ): Promise<PricingBundle> {
    const items = (bundle.changed ?? []).flatMap(
      (priceList) => priceList.items ?? [],
    );
    const names = new Map<string, string>();
    for (const item of (cached?.bundle?.changed ?? []).flatMap(
      (priceList) => priceList.items ?? [],
    )) {
      if (item.productCategory && item.productCategoryName)
        names.set(item.productCategory, item.productCategoryName);
    }
    const ids = [
      ...new Set(
        items
          .map((item) => item.productCategory)
          .filter((id): id is string => !!id),
      ),
    ];
    try {
      await this.lookUpCategoryNames(ids, names);
    } catch (error) {
      console.warn('PricingDataService: category names not refreshed', error);
    }
    for (const item of items)
      item.productCategoryName = item.productCategory
        ? (names.get(item.productCategory) ?? null)
        : null;
    return bundle;
  }

  /** Chunks run together; names from those that answered are kept when another fails. */
  private async lookUpCategoryNames(
    ids: string[],
    names: Map<string, string>,
  ): Promise<void> {
    const chunks: string[][] = [];
    for (let start = 0; start < ids.length; start += CATEGORY_LOOKUP_CHUNK)
      chunks.push(ids.slice(start, start + CATEGORY_LOOKUP_CHUNK));
    const answers = await Promise.allSettled(
      chunks.map(async (chunk) =>
        this.dynamics.retrieveAll<{ productid: string; name: string }>(
          'products',
          ['productid', 'name'],
          chunk.map((id) => `productid eq ${id}`).join(' or '),
        ),
      ),
    );
    for (const answer of answers)
      if (answer.status === 'fulfilled')
        for (const product of answer.value?.value ?? [])
          names.set(product.productid, product.name);
    const failed = answers.find(
      (answer): answer is PromiseRejectedResult => answer.status === 'rejected',
    );
    if (failed) throw failed.reason;
  }
}
